import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { ErrorTrackingService } from './error-tracking.service';
import { NotificationService } from '../../core/notification.service';
import { ToastService } from '../../core/toast.service';
import { environment } from '../../../environments/environment';

export interface ErrorContext {
  component?: string;
  action?: string;
  endpoint?: string;
  userInput?: any;
  formData?: any;
  errorType?: string;
  stack?: string;
  filename?: string;
  lineNumber?: number;
  columnNumber?: number;
  promiseRejection?: boolean;
  originalEvent?: string;
  originalMessage?: string;
  [key: string]: any;
}

export interface ProcessedError {
  id: string;
  type: 'http' | 'validation' | 'business' | 'unexpected';
  level: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  userMessage: string;
  technical: {
    stack?: string;
    httpStatus?: number;
    endpoint?: string;
    originalError: any;
    errorType?: string;
    filename?: string;
    lineNumber?: number;
    columnNumber?: number;
  };
  context: ErrorContext;
  notifiedToSupport: boolean;
  retryable: boolean;
  suggestions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private errorTracking = inject(ErrorTrackingService);
  private notification = inject(NotificationService);
  private toast = inject(ToastService);

  private readonly ERROR_MESSAGES = {
    // HTTP Errors
    400: 'Os dados enviados são inválidos. Verifique as informações e tente novamente.',
    401: 'Sua sessão expirou. Faça login novamente.',
    403: 'Você não tem permissão para realizar esta ação.',
    404: 'O recurso solicitado não foi encontrado.',
    409: 'Já existe um registro com essas informações.',
    422: 'Os dados fornecidos não puderam ser processados.',
    429: 'Muitas tentativas. Aguarde um momento antes de tentar novamente.',
    500: 'Erro interno do servidor. Nossa equipe foi notificada.',
    502: 'Serviço temporariamente indisponível. Tente novamente em instantes.',
    503: 'Serviço em manutenção. Tente novamente mais tarde.',

    // Validation Errors
    REQUIRED_FIELD: 'Este campo é obrigatório.',
    INVALID_EMAIL: 'Digite um email válido.',
    INVALID_PHONE: 'Digite um telefone válido.',
    INVALID_CNPJ: 'Digite um CNPJ válido.',
    INVALID_CPF: 'Digite um CPF válido.',
    PASSWORD_TOO_WEAK: 'A senha deve ter pelo menos 8 caracteres.',
    PASSWORDS_DONT_MATCH: 'As senhas não coincidem.',

    // Business Errors
    USER_ALREADY_EXISTS: 'Já existe um usuário com este email.',
    USERNAME_TAKEN: 'Este nome de usuário já está em uso.',
    INSUFFICIENT_PERMISSIONS: 'Você não tem permissão suficiente.',
    RESOURCE_NOT_FOUND: 'O recurso solicitado não foi encontrado.',
    OPERATION_NOT_ALLOWED: 'Esta operação não é permitida no momento.',

    // Network Errors
    NETWORK_ERROR: 'Problema de conexão. Verifique sua internet.',
    TIMEOUT_ERROR: 'A operação demorou muito. Tente novamente.',

    // Default
    UNEXPECTED_ERROR: 'Erro inesperado. Nossa equipe foi notificada.',
  };

  /**
   * Processa e trata um erro de forma unificada
   */
  handleError(error: any, context: ErrorContext = {}): ProcessedError {
    const processedError = this.processError(error, context);

    // Track error
    processedError.id = this.errorTracking.reportError({
      level: processedError.level,
      message: `${processedError.type.toUpperCase()}: ${
        processedError.message
      }`,
      stack: processedError.technical.stack,
      context: {
        ...processedError.context,
        type: processedError.type,
        httpStatus: processedError.technical.httpStatus,
        endpoint: processedError.technical.endpoint,
      },
    });

    // Always show notification to user regardless of level
    this.showUserNotification(processedError);

    return processedError;
  }

  /**
   * Shows notification to user based on error level
   */
  private showUserNotification(processedError: ProcessedError): void {
    try {
      const message = processedError.userMessage;
      const title = processedError.title;

      switch (processedError.level) {
        case 'error':
          this.toast.showError(message, title);
          break;
        case 'warning':
          this.toast.showWarning(message, title);
          break;
        case 'info':
          this.toast.showInfo(message, title);
          break;
        default:
          this.toast.showError(message, title);
          break;
      }

      // Also log to console in development
      if (!environment.production) {
        console.group(
          `🔔 User Notification [${processedError.level.toUpperCase()}]`
        );
        console.log('Title:', title);
        console.log('Message:', message);
        console.log('Error ID:', processedError.id);
        console.groupEnd();
      }
    } catch (notificationError) {
      console.error('Failed to show user notification:', notificationError);
      // Fallback: show browser alert for critical errors
      if (processedError.level === 'error') {
        alert(`Erro: ${processedError.userMessage}`);
      }
    }
  }

  /**
   * Trata especificamente erros HTTP
   */
  handleHttpError(
    error: HttpErrorResponse,
    context: ErrorContext = {}
  ): ProcessedError {
    const enhancedContext: ErrorContext = {
      ...context,
      endpoint: error.url || 'unknown',
      httpStatus: error.status,
      httpMessage: error.message,
    };

    return this.handleError(error, enhancedContext);
  }

  /**
   * Trata erros de validação
   */
  handleValidationError(
    field: string,
    errorType: string,
    context: ErrorContext = {}
  ): ProcessedError {
    const validationError = {
      name: 'ValidationError',
      message: `Validation failed for field: ${field}`,
      field,
      errorType,
    };

    const enhancedContext: ErrorContext = {
      ...context,
      type: 'validation',
      field,
      errorType,
    };

    return this.handleError(validationError, enhancedContext);
  }

  /**
   * Trata erros de negócio
   */
  handleBusinessError(
    businessRule: string,
    message: string,
    context: ErrorContext = {}
  ): ProcessedError {
    const businessError = {
      name: 'BusinessError',
      message,
      businessRule,
    };

    const enhancedContext: ErrorContext = {
      ...context,
      type: 'business',
      businessRule,
    };

    return this.handleError(businessError, enhancedContext);
  }

  /**
   * Cria um Observable de erro tratado
   */
  createErrorObservable(
    error: any,
    context: ErrorContext = {}
  ): Observable<never> {
    const processedError = this.handleError(error, context);
    return throwError(() => processedError);
  }

  /**
   * Processa erro em formato estruturado
   */
  private processError(error: any, context: ErrorContext): ProcessedError {
    // Use enhanced detection for global errors
    const errorType = this.detectGlobalErrorType(error, context);

    if (error instanceof HttpErrorResponse || errorType === 'http') {
      return this.processHttpError(error, context);
    }

    if (this.isValidationError(error) || errorType === 'validation') {
      return this.processValidationError(error, context);
    }

    if (this.isBusinessError(error) || errorType === 'business') {
      return this.processBusinessError(error, context);
    }

    if (this.isNetworkError(error)) {
      return this.processNetworkError(error, context);
    }

    return this.processUnexpectedError(error, context);
  }

  private processHttpError(
    error: HttpErrorResponse,
    context: ErrorContext
  ): ProcessedError {
    const status = error.status;
    const serverMessage = error.error?.message || error.error?.error;
    const userMessage =
      (this.ERROR_MESSAGES as any)[status] ||
      this.ERROR_MESSAGES.UNEXPECTED_ERROR;

    return {
      id: '',
      type: 'http',
      level: status >= 500 ? 'error' : 'warning',
      title: `Erro HTTP ${status}`,
      message: serverMessage || error.message,
      userMessage,
      technical: {
        httpStatus: status,
        endpoint: error.url || '',
        stack: error.error?.stack,
        originalError: error,
      },
      context: { ...context, httpStatus: status },
      notifiedToSupport: false,
      retryable: status >= 500 || status === 0,
      suggestions: this.getHttpErrorSuggestions(status),
    };
  }

  private processValidationError(
    error: any,
    context: ErrorContext
  ): ProcessedError {
    const errorType = error.errorType || 'INVALID_INPUT';
    const field = error.field || context['field'] || 'unknown';
    const userMessage =
      (this.ERROR_MESSAGES as any)[errorType] || 'Dados inválidos fornecidos.';

    return {
      id: '',
      type: 'validation',
      level: 'warning',
      title: 'Erro de Validação',
      message: error.message || `Validation error for ${field}`,
      userMessage,
      technical: {
        originalError: error,
      },
      context: { ...context, field, errorType },
      notifiedToSupport: false,
      retryable: false,
      suggestions: ['Verifique os dados informados', 'Consulte a documentação'],
    };
  }

  private processBusinessError(
    error: any,
    context: ErrorContext
  ): ProcessedError {
    const businessRule = error.businessRule || 'UNKNOWN_RULE';
    const userMessage =
      (this.ERROR_MESSAGES as any)[businessRule] ||
      error.message ||
      'Operação não permitida.';

    return {
      id: '',
      type: 'business',
      level: 'warning',
      title: 'Regra de Negócio',
      message: error.message,
      userMessage,
      technical: {
        originalError: error,
      },
      context: { ...context, businessRule },
      notifiedToSupport: false,
      retryable: false,
      suggestions: this.getBusinessErrorSuggestions(businessRule),
    };
  }

  private processNetworkError(
    error: any,
    context: ErrorContext
  ): ProcessedError {
    const isTimeout =
      error.name === 'TimeoutError' || error.message?.includes('timeout');
    const userMessage = isTimeout
      ? this.ERROR_MESSAGES.TIMEOUT_ERROR
      : this.ERROR_MESSAGES.NETWORK_ERROR;

    return {
      id: '',
      type: 'http',
      level: 'error',
      title: isTimeout ? 'Timeout' : 'Erro de Rede',
      message: error.message,
      userMessage,
      technical: {
        stack: error.stack,
        originalError: error,
      },
      context,
      notifiedToSupport: false,
      retryable: true,
      suggestions: [
        'Verifique sua conexão com a internet',
        'Tente novamente em alguns momentos',
        'Se o problema persistir, entre em contato com o suporte',
      ],
    };
  }

  private processUnexpectedError(
    error: any,
    context: ErrorContext
  ): ProcessedError {
    // Use enhanced message generation for global errors
    const userMessage = this.isGlobalError(context)
      ? this.generateGlobalErrorUserMessage(error, context)
      : this.ERROR_MESSAGES.UNEXPECTED_ERROR;

    // Enhance title based on context
    let title = 'Erro Inesperado';
    if (context.action === 'promise_rejection') {
      title = 'Erro de Promise';
    } else if (context.action === 'javascript_error') {
      title = 'Erro JavaScript';
    } else if (context.action === 'unhandled_error') {
      title = 'Erro Não Tratado';
    }

    return {
      id: '',
      type: 'unexpected',
      level: 'error',
      title,
      message: error.message || error.reason || 'Unexpected error occurred',
      userMessage,
      technical: {
        stack: error.stack || context.stack,
        originalError: error,
        errorType: context.errorType,
        filename: context.filename,
        lineNumber: context.lineNumber,
        columnNumber: context.columnNumber,
      },
      context,
      notifiedToSupport: this.isGlobalError(context), // Auto-notify for global errors
      retryable: false,
      suggestions: this.getUnexpectedErrorSuggestions(context),
    };
  }

  private isValidationError(error: any): boolean {
    return (
      error.name === 'ValidationError' ||
      error.type === 'validation' ||
      Boolean(error.field)
    );
  }

  private isBusinessError(error: any): boolean {
    return (
      error.name === 'BusinessError' ||
      error.type === 'business' ||
      Boolean(error.businessRule)
    );
  }

  private isNetworkError(error: any): boolean {
    return (
      error.name === 'NetworkError' ||
      error.name === 'TimeoutError' ||
      error.message?.includes('Network') ||
      error.message?.includes('timeout') ||
      (error instanceof TypeError && error.message.includes('fetch'))
    );
  }

  private getHttpErrorSuggestions(status: number): string[] {
    switch (status) {
      case 401:
        return ['Faça login novamente', 'Verifique suas credenciais'];
      case 403:
        return [
          'Entre em contato com o administrador',
          'Verifique suas permissões',
        ];
      case 404:
        return ['Verifique o endereço', 'Tente acessar pela página inicial'];
      case 409:
        return ['Use dados diferentes', 'Verifique se o registro já existe'];
      case 422:
        return ['Revise os dados informados', 'Consulte a documentação'];
      case 429:
        return ['Aguarde alguns minutos', 'Evite muitas tentativas seguidas'];
      case 500:
      case 502:
      case 503:
        return ['Tente novamente mais tarde', 'Nossa equipe foi notificada'];
      default:
        return ['Tente novamente', 'Entre em contato com o suporte'];
    }
  }

  private getBusinessErrorSuggestions(businessRule: string): string[] {
    switch (businessRule) {
      case 'USER_ALREADY_EXISTS':
        return ['Use um email diferente', 'Faça login se já tem conta'];
      case 'USERNAME_TAKEN':
        return [
          'Escolha outro nome de usuário',
          'Adicione números ou caracteres',
        ];
      case 'INSUFFICIENT_PERMISSIONS':
        return [
          'Entre em contato com o administrador',
          'Verifique seu perfil de acesso',
        ];
      default:
        return ['Revise a operação', 'Consulte a documentação'];
    }
  }

  /**
   * Get context-aware suggestions for unexpected errors
   */
  private getUnexpectedErrorSuggestions(context: ErrorContext): string[] {
    const baseSuggestions = [
      'Recarregue a página',
      'Tente novamente mais tarde',
    ];

    if (context.action === 'promise_rejection') {
      return [
        'Verifique sua conexão com a internet',
        'Aguarde alguns segundos e tente novamente',
        ...baseSuggestions,
      ];
    }

    if (context.action === 'javascript_error') {
      return [
        'Atualize seu navegador',
        'Limpe o cache do navegador',
        ...baseSuggestions,
        'Entre em contato com o suporte técnico',
      ];
    }

    if (this.isGlobalError(context)) {
      return [
        'Nossa equipe foi automaticamente notificada',
        ...baseSuggestions,
        'Entre em contato com o suporte se o problema persistir',
      ];
    }

    return [
      ...baseSuggestions,
      'Entre em contato com o suporte se o problema persistir',
    ];
  }

  /**
   * Determines if error came from global handler
   */
  private isGlobalError(context: ErrorContext): boolean {
    return (
      context.component === 'GlobalErrorHandler' ||
      context.component === 'UnhandledPromiseRejectionHandler' ||
      context.component === 'UnhandledJavaScriptErrorHandler'
    );
  }

  /**
   * Enhanced error detection for global errors
   */
  private detectGlobalErrorType(
    error: any,
    context: ErrorContext
  ): 'http' | 'validation' | 'business' | 'unexpected' {
    // If it's explicitly from global handlers, it's likely unexpected
    if (this.isGlobalError(context)) {
      // Check if it's actually an HTTP error that bubbled up
      if (error instanceof HttpErrorResponse || error?.status !== undefined) {
        return 'http';
      }

      // Check for validation-like errors
      if (
        error?.name === 'ValidationError' ||
        context.errorType === 'validation' ||
        error?.message?.toLowerCase().includes('validation')
      ) {
        return 'validation';
      }

      // Check for business rule errors
      if (
        error?.businessRule ||
        context.errorType === 'business' ||
        error?.message?.toLowerCase().includes('business')
      ) {
        return 'business';
      }

      // Default to unexpected for global errors
      return 'unexpected';
    }

    // Use existing detection logic for non-global errors
    if (error instanceof HttpErrorResponse) return 'http';
    if (error?.name === 'ValidationError') return 'validation';
    if (error?.businessRule) return 'business';
    return 'unexpected';
  }

  /**
   * Enhanced user message generation for global errors
   */
  private generateGlobalErrorUserMessage(
    error: any,
    context: ErrorContext
  ): string {
    // If from global handlers, provide more context-aware messages
    if (this.isGlobalError(context)) {
      // Promise rejections
      if (context.promiseRejection) {
        return 'Ocorreu um erro durante o processamento. Tente novamente.';
      }

      // JavaScript errors
      if (context.action === 'javascript_error') {
        return 'Erro inesperado na aplicação. Recarregue a página se o problema persistir.';
      }

      // General unhandled errors
      if (context.action === 'unhandled_error') {
        return 'Erro não tratado detectado. Nossa equipe foi notificada.';
      }
    }

    // Use fallback message for non-global errors
    return this.ERROR_MESSAGES.UNEXPECTED_ERROR;
  }

  /**
   * Generate user-friendly message for various error types
   */
  private generateUserMessage(error: any): string {
    // HTTP Error Response
    if (error instanceof HttpErrorResponse) {
      const statusMessage = (this.ERROR_MESSAGES as any)[error.status];
      if (statusMessage) {
        return statusMessage;
      }
    }

    // Business rule errors
    if (error?.businessRule) {
      const businessMessage = (this.ERROR_MESSAGES as any)[error.businessRule];
      if (businessMessage) {
        return businessMessage;
      }
    }

    // Validation errors
    if (error?.name === 'ValidationError') {
      return 'Os dados informados são inválidos. Verifique e tente novamente.';
    }

    // Default fallback message
    return this.ERROR_MESSAGES.UNEXPECTED_ERROR;
  }
}
